import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Brain, Database, FileText, Bot, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import DataTable from '../components/DataTable';

// Configure axios to include credentials for authentication
axios.defaults.withCredentials = true;

const AIAnswer = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [translatedQueries, setTranslatedQueries] = useState(null);
  const [finalReport, setFinalReport] = useState(null);
  const [processingSteps, setProcessingSteps] = useState({
    translation: 'pending', // pending, processing, completed, failed
    execution: 'pending',
    summary: 'pending'
  });
  const [queryExecutionStatus, setQueryExecutionStatus] = useState({}); // Track individual query status
  const timerIntervalsRef = useRef({}); // Track timer intervals for cleanup
  const pollingIntervalRef = useRef(null); // Track polling interval for cleanup
  const [currentSessionId, setCurrentSessionId] = useState(null); // Track current session
  
  // Multi-tab state management - start with Executive Summary
  const [activeSummaryTab, setActiveSummaryTab] = useState('executive-summary'); // executive-summary, key-insights, financial-analysis, etc.
  const [activeDataTab, setActiveDataTab] = useState('vista'); // vista, procore, safetyAnalyst, fileSearch

  // Slide generation tracking (similar to other components)
  const [slideLoading, setSlideLoading] = useState(false);
  const [slideProgress, setSlideProgress] = useState({});
  const [slideGenerationId, setSlideGenerationId] = useState(null);
  const [slideCompletionNotified, setSlideCompletionNotified] = useState(false);
  const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);

  // Entity extraction and clarification state
  const [entityExtraction, setEntityExtraction] = useState(null);
  const [clarificationQuestions, setClarificationQuestions] = useState([]);
  const [userResponses, setUserResponses] = useState({});
  const [showClarification, setShowClarification] = useState(false);
  const [isEntityExtracting, setIsEntityExtracting] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [refinedQuery, setRefinedQuery] = useState('');

  // Global interval tracking to prevent orphaned polling
  if (!window.allPollingIntervals) {
    window.allPollingIntervals = new Set();
  }

  // Emergency cleanup function (accessible from browser console)
  window.stopAllPolling = () => {
    console.log('🚨 EMERGENCY CLEANUP: Stopping ALL polling intervals');
    
    if (window.allPollingIntervals && window.allPollingIntervals.size > 0) {
      console.log(`🛑 Emergency: Clearing ${window.allPollingIntervals.size} tracked intervals`);
      
      window.allPollingIntervals.forEach(intervalId => {
        clearInterval(intervalId);
        console.log(`🛑 Emergency: Cleared interval ${intervalId}`);
      });
      
      window.allPollingIntervals.clear();
    }
    
    if (window.activePollingInterval) {
      clearInterval(window.activePollingInterval);
      window.activePollingInterval = null;
      console.log('🛑 Emergency: Cleared window.activePollingInterval');
    }
    
    console.log('✅ Emergency cleanup complete!');
  };

  // Parse JSON report into structured sections for multi-tab display
  const parseReportSections = (reportData) => {
    if (!reportData) return [];
    
    // Handle both JSON objects and string formats
    let parsedData;
    
    if (typeof reportData === 'object' && reportData !== null) {
      // Already a JSON object - use directly
      parsedData = reportData;
      console.log('🔍 Using JSON object directly:', Object.keys(parsedData));
    } else if (typeof reportData === 'string') {
      try {
        // Try to extract JSON from markdown response
        const jsonMatch = reportData.match(/```json\s*(\{[\s\S]*?\})\s*```/);
        if (jsonMatch) {
          parsedData = JSON.parse(jsonMatch[1]);
          console.log('🔍 Parsed JSON from code block');
        } else {
          // Try to parse the entire string as JSON
          parsedData = JSON.parse(reportData);
          console.log('🔍 Parsed entire string as JSON');
        }
      } catch (e) {
        console.log('⚠️ Failed to parse JSON, attempting to fix common issues:', e.message);
        
        // Try to fix common JSON formatting issues
        try {
          let fixedJson = reportData;
          
          // Fix unescaped newlines in JSON fields
          fixedJson = fixedJson.replace(/"\s*\n\s*•/g, '\\n•'); // Fix bullet points
          fixedJson = fixedJson.replace(/"\s*\n\s*"/g, '","');   // Fix line breaks between fields
          fixedJson = fixedJson.replace(/,\s*\n\s*}/g, '\n}');   // Fix trailing commas
          
          // Try to extract just the JSON object part
          const jsonMatch = fixedJson.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            parsedData = JSON.parse(jsonMatch[0]);
            console.log('✅ Successfully parsed fixed JSON');
          } else {
            throw new Error('No JSON object found after fixes');
          }
        } catch (fixError) {
          console.log('⚠️ JSON fix failed, treating as markdown:', fixError.message);
          // Final fallback to treating as markdown
          return [
            { id: 'full-report', title: 'Full Report', content: reportData, icon: '📋' }
          ];
        }
      }
    } else {
      console.log('⚠️ Unknown reportData type:', typeof reportData);
      return [
        { id: 'full-report', title: 'Full Report', content: 'Invalid report format', icon: '📋' }
      ];
    }
    
    // Create exactly 5 structured sections from JSON
    const sections = [
      {
        id: 'executive-summary',
        title: 'Executive Summary', 
        content: Array.isArray(parsedData.executiveSummary) ? 
          parsedData.executiveSummary.map(item => `• ${item}`).join('\n') : 
          typeof parsedData.executiveSummary === 'string' ? 
            parsedData.executiveSummary.replace(/\\n/g, '\n') : 
            'No executive summary available',
        icon: '📊'
      },
      {
        id: 'key-insights', 
        title: 'Key Insights',
        content: Array.isArray(parsedData.keyInsights) ? 
          parsedData.keyInsights.map(insight => `• ${insight}`).join('\n') : 
          parsedData.keyInsights || 'No insights available',
        icon: '🔍'
      },
      {
        id: 'financial-analysis',
        title: 'Financial Analysis',
        content: typeof parsedData.financialAnalysis === 'string' ? 
          parsedData.financialAnalysis.replace(/\\n/g, '\n') : 
          Array.isArray(parsedData.financialAnalysis) ?
            parsedData.financialAnalysis.map(item => `• ${item}`).join('\n') :
            'No financial analysis available',
        icon: '💰'
      },
      {
        id: 'operational-analysis', 
        title: 'Operational Analysis',
        content: typeof parsedData.operationalAnalysis === 'string' ? 
          parsedData.operationalAnalysis.replace(/\\n/g, '\n') : 
          Array.isArray(parsedData.operationalAnalysis) ?
            parsedData.operationalAnalysis.map(item => `• ${item}`).join('\n') :
            'No operational analysis available',
        icon: '🏗️'
      },
      {
        id: 'safety-analysis',
        title: 'Safety Analysis',
        content: typeof parsedData.safetyAnalysis === 'string' ? 
          parsedData.safetyAnalysis.replace(/\\n/g, '\n') : 
          Array.isArray(parsedData.safetyAnalysis) ?
            parsedData.safetyAnalysis.map(item => `• ${item}`).join('\n') :
            'No safety analysis available',
        icon: '🛡️'
      },
      {
        id: 'recommendations',
        title: 'Recommendations',
        content: Array.isArray(parsedData.recommendations) ?
          parsedData.recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n\n') :
          parsedData.recommendations || 'No recommendations available',
        icon: '💡'
      }
    ];
    
    return sections;
  };

  // Get icon for section based on title (enhanced mapping)
  const getIconForSection = (title) => {
    const titleLower = title.toLowerCase();
    
    // Exact matches first (most specific)
    if (titleLower === 'executive summary') return '📊';
    if (titleLower === 'key insights') return '🔍';
    if (titleLower === 'financial analysis') return '💰';
    if (titleLower === 'operational analysis') return '🏗️';
    if (titleLower === 'safety analysis') return '🛡️';
    if (titleLower === 'document insights') return '📄';
    if (titleLower === 'recommendations') return '💡';
    
    // Partial matches (fallback)
    if (titleLower.includes('executive') || titleLower.includes('summary')) return '📊';
    if (titleLower.includes('insight') || titleLower.includes('finding') || titleLower.includes('key')) return '🔍';
    if (titleLower.includes('financial') || titleLower.includes('cost') || titleLower.includes('vista') || titleLower.includes('budget')) return '💰';
    if (titleLower.includes('operational') || titleLower.includes('procore') || titleLower.includes('schedule') || titleLower.includes('rfi')) return '🏗️';
    if (titleLower.includes('safety') || titleLower.includes('incident') || titleLower.includes('soar')) return '🛡️';
    if (titleLower.includes('document') || titleLower.includes('file') || titleLower.includes('search')) return '📄';
    if (titleLower.includes('recommendation') || titleLower.includes('action') || titleLower.includes('next step')) return '💡';
    if (titleLower.includes('analysis') || titleLower.includes('data')) return '📈';
    
    return '📋'; // Default
  };

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      Object.values(timerIntervalsRef.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
      
      // Clean up polling intervals
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (window.activePollingInterval) {
        clearInterval(window.activePollingInterval);
        window.activePollingInterval = null;
      }
    };
  }, []);

  // Auto-select first meaningful section when report loads
  useEffect(() => {
    if (finalReport?.report) {
      const sections = parseReportSections(finalReport.report);
      
      // Prefer Executive Summary, then first available section
      const executiveSummary = sections.find(s => s.title.toLowerCase().includes('executive') || s.title.toLowerCase().includes('summary'));
      const firstSection = sections.find(s => s.id !== 'full-report'); // Skip full report
      
      const defaultSection = executiveSummary || firstSection || sections[0];
      
      if (defaultSection && activeSummaryTab !== defaultSection.id) {
        console.log(`🎯 Auto-selecting summary tab: ${defaultSection.title} (${defaultSection.id})`);
        setActiveSummaryTab(defaultSection.id);
      }
    }
  }, [finalReport?.report]);

  // Function to execute queries with simulated progress updates + real backend sync
  const executeQueriesWithRealTimeUpdates = async (translatedQueries) => {
    console.log('🔍 executeQueriesWithRealTimeUpdates called with:', translatedQueries);
    const totalQueries = (translatedQueries.procore?.length || 0) + (translatedQueries.vista?.length || 0) + (translatedQueries.safetyAnalyst?.length || 0) + (translatedQueries.fileSearch?.length || 0);
    
    console.log(`🚀 Starting ${totalQueries} queries with real backend polling...`);
    console.log('📊 Query breakdown:', {
      procore: translatedQueries.procore?.length || 0,
      vista: translatedQueries.vista?.length || 0,
      safetyAnalyst: translatedQueries.safetyAnalyst?.length || 0,
      fileSearch: translatedQueries.fileSearch?.length || 0
    });
    
    // Polling control variables
    let pollingInterval;
    let sessionId;
    let pollAttempts = 0;
    const maxPollAttempts = 60; // Max 10 minutes of polling (60 attempts × 10 seconds)
    
    try {
      // Start backend execution (don't wait for completion)
      console.log('🚀 Starting backend execution...');
      
      const executionPromise = axios.post('/api/ai-answer/execute', {
        translatedQueries: translatedQueries
      }, {
        timeout: 300000 // 5 minute timeout
      });
      
      // Get sessionId from backend immediately (backend now returns right away)
      console.log('⏳ Getting sessionId from backend...');
      const initialResponse = await executionPromise;
      
      if (!initialResponse.data.success) {
        throw new Error(initialResponse.data.error || 'Failed to start execution');
      }
      
      sessionId = initialResponse.data.sessionId;
      setCurrentSessionId(sessionId); // Track current session
      console.log(`✅ Got real sessionId: ${sessionId}, starting polling every 10 seconds...`);
      console.log(`🔍 SESSION TRACKING: Current session is ${sessionId}`);
      
      // Start polling for real status updates with proper tracking
      pollingInterval = setInterval(async () => {
        pollAttempts++;
        console.log(`📊 Polling status for session ${sessionId} (attempt ${pollAttempts}/${maxPollAttempts})...`);
        
        // Stop polling if max attempts reached
        if (pollAttempts >= maxPollAttempts) {
          console.log(`⏰ Max polling attempts (${maxPollAttempts}) reached - stopping`);
          clearInterval(pollingInterval);
          pollingIntervalRef.current = null;
          window.activePollingInterval = null;
          return;
        }
        
        try {
          // Poll backend for real status
          const statusResponse = await axios.get(`/api/ai-answer/execution-status/${sessionId}`, {
            timeout: 10000
          });
          
          // Stop polling if session not found
          if (!statusResponse.data.success) {
            console.log(`⚠️ Session ${sessionId} not found - stopping polling`);
            clearInterval(pollingInterval);
            pollingIntervalRef.current = null;
            window.activePollingInterval = null;
            return;
          }
          
          if (statusResponse.data.success && statusResponse.data.progress) {
            const { progress } = statusResponse.data;
            
            // Stop polling if progress data is null/empty (session expired)
            if (!progress || progress.total === null || progress.total === undefined) {
              console.log(`⚠️ Session ${sessionId} has no progress data - stopping polling`);
              clearInterval(pollingInterval);
              pollingIntervalRef.current = null;
              window.activePollingInterval = null;
              return;
            }
            
            console.log(`📈 Real progress update: ${progress.completed}/${progress.total} completed`);
            console.log('🔍 Progress data:', progress);
            console.log('🔍 Queries in progress:', progress.queries);
            
            // Update UI with real backend status
            setQueryExecutionStatus(prev => {
              const updated = { ...prev };
              
              progress.queries?.forEach(query => {
                if (updated[query.id]) {
                  updated[query.id] = {
                    ...updated[query.id],
                    status: query.status,
                    endTime: query.endTime,
                    hasResult: query.hasResult,
                    hasError: query.hasError
                  };
                  console.log(`🔄 Updated ${query.id}: ${query.status}`);
                }
              });
              
              return updated;
            });
            
            // Stop polling when all queries are done (completed + failed = total)
            const allQueriesDone = (progress.completed + progress.failed) >= progress.total;
            if (statusResponse.data.status === 'completed' || allQueriesDone) {
                console.log('✅ Polling complete - all queries finished');
                clearInterval(pollingInterval);
                pollingIntervalRef.current = null;
                window.activePollingInterval = null;
              
              // Return status data as execution results for downstream processing
              return {
                vista: progress.queries?.filter(q => q.system === 'vista') || [],
                procore: progress.queries?.filter(q => q.system === 'procore') || [],
                fileSearch: progress.queries?.filter(q => q.system === 'fileSearch') || [],
                metadata: {
                  totalQueries: progress.total || 0,
                  successfulQueries: progress.completed || 0,
                  failedQueries: progress.failed || 0
                }
              };
            }
          }
        } catch (pollError) {
          console.warn('⚠️ Polling failed:', pollError.message);
          
          // If it's a 404 or auth error, stop polling
          if (pollError.response?.status === 404 || pollError.response?.status === 401 || pollError.response?.status === 403) {
            console.log(`🛑 Stopping polling due to ${pollError.response?.status} error`);
            clearInterval(pollingInterval);
            pollingIntervalRef.current = null;
            window.activePollingInterval = null;
          }
        }
      }, 10000); // Poll every 10 seconds
      
      // Store polling interval in multiple places for comprehensive cleanup
      pollingIntervalRef.current = pollingInterval;
      window.activePollingInterval = pollingInterval;
      window.allPollingIntervals.add(pollingInterval); // Track globally
      console.log(`🔄 Polling stored for cleanup: ref=${!!pollingIntervalRef.current}, global=${!!window.activePollingInterval}, total tracked=${window.allPollingIntervals.size}`);
      console.log(`🔍 New interval ID ${pollingInterval} for session ${sessionId}`);
      
      // Wait for polling to complete (when status becomes 'completed')
      return new Promise((resolve) => {
        const checkCompletion = setInterval(() => {
          // This will be resolved by the polling interval when status === 'completed'
          // The polling interval will call clearInterval and resolve with results
        }, 1000);
        
        // Override the polling interval to resolve the promise when complete
        const originalInterval = pollingInterval;
        pollingInterval = setInterval(async () => {
          pollAttempts++;
          console.log(`📊 Polling status for session ${sessionId} (attempt ${pollAttempts}/${maxPollAttempts})...`);
          
          // Stop polling if max attempts reached
          if (pollAttempts >= maxPollAttempts) {
            console.log(`⏰ Max polling attempts (${maxPollAttempts}) reached - stopping (second interval)`);
            clearInterval(pollingInterval);
            clearInterval(checkCompletion);
            pollingIntervalRef.current = null;
            window.activePollingInterval = null;
            return resolve({ error: 'Polling timeout' });
          }
          
          try {
            
            const statusResponse = await axios.get(`/api/ai-answer/execution-status/${sessionId}`, {
              timeout: 10000
            });
            
            // Stop polling if session not found
            if (!statusResponse.data.success) {
              console.log(`⚠️ Session ${sessionId} not found - stopping polling (second interval)`);
              clearInterval(pollingInterval);
              clearInterval(checkCompletion);
              pollingIntervalRef.current = null;
              window.activePollingInterval = null;
              return resolve({ error: 'Session not found' });
            }
            
            if (statusResponse.data.success && statusResponse.data.progress) {
              const { progress } = statusResponse.data;
              
              // Stop polling if progress data is null/empty (session expired)
              if (!progress || progress.total === null || progress.total === undefined) {
                console.log(`⚠️ Session ${sessionId} has no progress data - stopping polling (second interval)`);
                clearInterval(pollingInterval);
                clearInterval(checkCompletion);
                pollingIntervalRef.current = null;
                window.activePollingInterval = null;
                return resolve({ error: 'Session expired' });
              }
              
              console.log(`📈 Real progress update: ${progress.completed}/${progress.total} completed`);
              
              // Update UI with real backend status
              setQueryExecutionStatus(prev => {
                const updated = { ...prev };
                
                progress.queries?.forEach(query => {
                  if (updated[query.id]) {
                    updated[query.id] = {
                      ...updated[query.id],
                      status: query.status,
                      endTime: query.endTime,
                      hasResult: query.hasResult,
                      hasError: query.hasError
                    };
                    console.log(`🔄 Updated ${query.id}: ${query.status}`);
                  }
                });
                
                return updated;
              });
              
              // Stop polling when all queries are done (completed + failed = total)
              const allQueriesDone = (progress.completed + progress.failed) >= progress.total;
              if (statusResponse.data.status === 'completed' || allQueriesDone) {
                console.log('✅ Polling complete - all queries finished');
                clearInterval(pollingInterval);
                clearInterval(checkCompletion);
                pollingIntervalRef.current = null;
                window.activePollingInterval = null;
                
                // Resolve with execution results including actual data
                const executionResults = {
                  vista: progress.queries?.filter(q => q.system === 'vista').map(q => ({
                    queryId: q.id,
                    system: q.system,
                    query: q.query,
                    success: q.success,
                    data: q.data || [],
                    response: q.response || q.explanation || q.summary || '',
                    resultCount: q.resultCount || 0,
                    error: q.error,
                    sqlQuery: q.sqlQuery || null, // Include SQL for debugging
                    columns: q.columns || [],
                    timestamp: new Date().toISOString()
                  })) || [],
                  procore: progress.queries?.filter(q => q.system === 'procore').map(q => ({
                    queryId: q.id,
                    system: q.system,
                    query: q.query,
                    success: q.success,
                    data: q.data || [],
                    response: q.response || q.explanation || q.summary || '',
                    resultCount: q.resultCount || 0,
                    error: q.error,
                    sqlQuery: q.sqlQuery || null, // Include SQL for debugging
                    timestamp: new Date().toISOString()
                  })) || [],
                  safetyAnalyst: progress.queries?.filter(q => q.system === 'safetyAnalyst').map(q => ({
                    queryId: q.id,
                    system: q.system,
                    query: q.query,
                    success: q.success,
                    data: q.data || [],
                    analysis: q.analysis || '',
                    explanation: q.explanation || '',
                    response: q.response || q.explanation || q.summary || '',
                    resultCount: q.resultCount || 0,
                    error: q.error,
                    sqlQuery: q.sqlQuery || null,
                    columns: q.columns || [],
                    followUpQuestions: q.followUpQuestions || [],
                    timestamp: new Date().toISOString()
                  })) || [],
                  fileSearch: progress.queries?.filter(q => q.system === 'fileSearch').map(q => ({
                    queryId: q.id,
                    system: q.system,
                    query: q.query,
                    success: q.success,
                    results: q.results || q.data || [], // File search uses 'results' field
                    summary: q.response || q.summary || '',
                    resultCount: q.resultCount || 0,
                    filesProcessed: q.filesProcessed || 0,
                    error: q.error,
                    timestamp: new Date().toISOString()
                  })) || [],
                  metadata: {
                    totalQueries: progress.total || 0,
                    successfulQueries: progress.completed || 0,
                    failedQueries: progress.failed || 0
                  }
                };
                
                console.log(`📊 Final results:`, {
                  vista: executionResults.vista.length,
                  procore: executionResults.procore.length,
                  fileSearch: executionResults.fileSearch.length
                });
                
                resolve(executionResults);
              }
            }
          } catch (pollError) {
            console.warn('⚠️ Polling failed (second interval):', pollError.message);
            
            // If it's a 404 or auth error, stop polling
            if (pollError.response?.status === 404 || pollError.response?.status === 401 || pollError.response?.status === 403) {
              console.log(`🛑 Stopping second polling due to ${pollError.response?.status} error`);
              clearInterval(pollingInterval);
              clearInterval(checkCompletion);
              pollingIntervalRef.current = null;
              window.activePollingInterval = null;
              return resolve({ error: `Polling stopped: ${pollError.response?.status}` });
            }
          }
      }, 10000); // Poll every 10 seconds
      });

    } catch (error) {
      // Stop polling on error
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingIntervalRef.current = null;
        window.activePollingInterval = null;
        console.log('🛑 Stopped polling due to error');
      }
      
      console.error('❌ Query execution failed:', error);
      
      // Mark all executing queries as failed
      setQueryExecutionStatus(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (updated[key].status === 'executing') {
            updated[key] = {
              ...updated[key],
              status: 'failed',
              error: error.message,
              endTime: Date.now()
            };
          }
        });
        return updated;
      });
      
      throw error;
    }
  };

  // Handle clarification responses and continue with main processing
  const handleClarificationSubmit = async () => {
    if (isProcessing) return;

    // Validate that all clarification questions are answered (if any)
    if (clarificationQuestions.length > 0) {
      const unansweredQuestions = clarificationQuestions.filter((_, index) => !userResponses[index]);
      if (unansweredQuestions.length > 0) {
        toast.error(`Please answer all ${unansweredQuestions.length} remaining questions`);
        return;
      }
    }

    setIsProcessing(true);
    setShowClarification(false);
    setCurrentStep('Processing your refined query...');

    try {
      console.log('🔄 Processing refined query...');
      
      // If we have projects and a refined query, use the refined query directly
      const queryToUse = availableProjects.length > 0 ? refinedQuery : query;
      console.log('🔄 Using refined query:', queryToUse);

      // Update the query state with the refined version
      setQuery(queryToUse);
      
      // Set up processing steps (skip entity extraction since we already did it)
      setProcessingSteps({
        entityExtraction: 'completed',
        translation: 'processing',
        execution: 'pending',
        summary: 'pending'
      });
      
      // Step 1: AI Translation Agent (skip entity extraction)
      setCurrentStep('🧠 Step 1: AI Translation - Converting your refined query into atomic searches...');
      
      const translationResponse = await axios.post('/api/ai-answer/translate', {
        query: queryToUse
      }, {
        timeout: 300000
      });

      if (!translationResponse.data.success) {
        throw new Error(translationResponse.data.error || 'Translation failed');
      }

      const translatedQueries = translationResponse.data.translatedQueries;
      setTranslatedQueries(translatedQueries);
      setProcessingSteps(prev => ({ ...prev, translation: 'completed', execution: 'processing' }));
      
      // Small delay to show translation completion
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: AI Execution Agent with real-time updates
      // Initialize query execution status for real-time updates
      const initialStatus = {};
      if (translatedQueries.vista) {
        translatedQueries.vista.forEach((query, index) => {
          initialStatus[`vista_${index}`] = {
            id: `vista_${index}`,
            system: 'vista',
            query: query.query,
            status: 'pending',
            progress: 0
          };
        });
      }
      if (translatedQueries.procore) {
        translatedQueries.procore.forEach((query, index) => {
          initialStatus[`procore_${index}`] = {
            id: `procore_${index}`,
            system: 'procore',
            query: query.query,
            status: 'pending',
            progress: 0
          };
        });
      }
      if (translatedQueries.safetyAnalyst) {
        translatedQueries.safetyAnalyst.forEach((query, index) => {
          initialStatus[`safetyAnalyst_${index}`] = {
            id: `safetyAnalyst_${index}`,
            system: 'safetyAnalyst',
            query: query.query,
            status: 'pending',
            progress: 0
          };
        });
      }
      if (translatedQueries.fileSearch) {
        translatedQueries.fileSearch.forEach((query, index) => {
          initialStatus[`fileSearch_${index}`] = {
            id: `fileSearch_${index}`,
            system: 'fileSearch',
            query: query.query,
            status: 'pending',
            progress: 0
          };
        });
      }
      setQueryExecutionStatus(initialStatus);
      console.log('🔍 Initialized query execution status for refined query:', initialStatus);
      
      const executionResults = await executeQueriesWithRealTimeUpdates(translatedQueries);
      
      setProcessingSteps(prev => ({ ...prev, execution: 'completed', summary: 'processing' }));
      setCurrentStep('📝 Step 3: AI Summary - Generating comprehensive analysis report...');
      
      // Small delay to show execution completion  
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 3: AI Summary Agent
      const summaryResponse = await axios.post('/api/ai-answer/summarize', {
        originalQuery: queryToUse,
        executionResults: executionResults
      }, {
        timeout: 300000 // 5 minute timeout for summary
      });

      if (!summaryResponse.data.success) {
        throw new Error(summaryResponse.data.error || 'Summary generation failed');
      }

      // Show final results with proper structure
      const summaryData = summaryResponse.data;
      const finalReportData = {
        report: summaryData.summaryReport || summaryData.report,
        structuredData: summaryData.structuredData,
        metadata: summaryData.metadata
      };
      
      setFinalReport(finalReportData);
      console.log('🔍 Final Report Structured Data:', finalReportData.structuredData);
      console.log('🛡️ Safety Analyst Data:', finalReportData.structuredData?.safetyAnalyst);
      setProcessingSteps({ translation: 'completed', execution: 'completed', summary: 'completed' });
      setCurrentStep('✅ Analysis complete!');
      
      // Generate follow-up questions
      const contextualQuestions = generateFollowUpQuestions(finalReportData.report, finalReportData.structuredData);
      setFollowUpQuestions(contextualQuestions);
      setShowFollowUpQuestions(true);
      
      console.log('✅ AI Answer processing completed successfully');
      toast.success('Analysis completed successfully!');

    } catch (error) {
      console.error('❌ Clarification processing error:', error);
      toast.error(`Clarification processing failed: ${error.message}`);
      setCurrentStep('Clarification processing failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setCurrentStep('');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    // AGGRESSIVE state cleanup - clear ALL possible timers and polling
    console.log('🧹 AGGRESSIVE cleanup of ALL timers and polling...');
    
    // Clear all possible polling references
    if (window.activePollingInterval) {
      clearInterval(window.activePollingInterval);
      window.activePollingInterval = null;
      console.log('🛑 Cleared window.activePollingInterval');
    }
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('🛑 Cleared pollingIntervalRef.current');
    }
    
    // Clear ALL tracked polling intervals globally (better than brute force)
    if (window.allPollingIntervals && window.allPollingIntervals.size > 0) {
      console.log(`🛑 Clearing ${window.allPollingIntervals.size} tracked polling intervals...`);
      
      window.allPollingIntervals.forEach(intervalId => {
        clearInterval(intervalId);
        console.log(`🛑 Cleared tracked interval: ${intervalId}`);
      });
      
      window.allPollingIntervals.clear();
      console.log('✅ All tracked intervals cleared and set emptied');
    }
    
    // Add a small delay to ensure cleanup completes
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Clear all timer intervals
    Object.values(timerIntervalsRef.current || {}).forEach(interval => {
      if (interval) clearInterval(interval);
    });
    timerIntervalsRef.current = {};
    
    // Reset ALL state
    setIsProcessing(true);
    setTranslatedQueries(null);
    setFinalReport(null);
    setQueryExecutionStatus({}); // Reset query status completely
    setShowFollowUpQuestions(false); // Hide previous follow-up questions
    setFollowUpQuestions([]);
    setEntityExtraction(null);
    setClarificationQuestions([]);
    setUserResponses({});
    setShowClarification(false);
    setIsEntityExtracting(false);
    setAvailableProjects([]);
    setSelectedProjects([]);
    setRefinedQuery('');
    setProcessingSteps({
      entityExtraction: 'processing',
      translation: 'pending',
      execution: 'pending',
      summary: 'pending'
    });
    setCurrentStep('Starting AI translation of your query...');
    
    console.log('✅ State reset complete - starting fresh query processing');

    try {
      console.log(`🚀 Processing AI Answer step by step for: "${query}"`);
      
      // Step 0: Entity Extraction and Clarification
      setCurrentStep('🔍 Step 0: Entity Extraction - Analyzing your query for project-specific information...');
      setIsEntityExtracting(true);
      
      // Start entity extraction timer (120 seconds)
      const entityStartTime = Date.now();
      const entityMaxTime = 120; // 120 seconds
      
      const entityTimerInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - entityStartTime) / 1000);
        const remaining = Math.max(0, entityMaxTime - elapsed);
        const progress = Math.min(100, (elapsed / entityMaxTime) * 100);
        
        setCurrentStep(`🔍 Step 0: Entity Extraction - Analyzing your query for project-specific information... (${elapsed}s elapsed, ${remaining}s remaining)`);
        
        if (elapsed >= entityMaxTime) {
          clearInterval(entityTimerInterval);
        }
      }, 1000);
      
      // Store timer for cleanup
      timerIntervalsRef.current.entityExtraction = entityTimerInterval;
      
           const entityResponse = await axios.post('/api/ai-answer/extract-entities', {
             query: query.trim()
           }, {
             timeout: 300000 // 5 minute timeout for entity extraction
           });

      if (!entityResponse.data.success) {
        throw new Error(entityResponse.data.error || 'Entity extraction failed');
      }

      const entityData = entityResponse.data;
      setEntityExtraction(entityData);
      setAvailableProjects(entityData.availableProjects || []);
      setSelectedProjects([]);
      setRefinedQuery(entityData.refinedQuery || query);
      
      
      // Check if clarification is needed OR if projects were found for review
      console.log('🔍 Entity extraction result:', {
        needsClarification: entityData.needsClarification,
        hasAvailableProjects: (entityData.availableProjects && entityData.availableProjects.length > 0),
        availableProjectsCount: (entityData.availableProjects || []).length,
        hasRefinedQuery: !!entityData.refinedQuery,
        refinedQuery: entityData.refinedQuery
      });
      
      if (entityData.needsClarification || (entityData.availableProjects && entityData.availableProjects.length > 0)) {
        console.log('✅ Showing clarification/review section');
        setClarificationQuestions(entityData.clarificationQuestions || []);
        setShowClarification(true);
        setIsEntityExtracting(false);
        setIsProcessing(false);
        setCurrentStep('Please review the project data and provide any clarification needed...');
        
        // Clear entity extraction timer
        if (timerIntervalsRef.current.entityExtraction) {
          clearInterval(timerIntervalsRef.current.entityExtraction);
          delete timerIntervalsRef.current.entityExtraction;
        }
        
        return; // Stop here and wait for user review/clarification
      } else {
        console.log('❌ NOT showing clarification/review section - no projects found or clarification needed');
      }

      // No clarification needed, proceed with main processing
      console.log('✅ No clarification needed, proceeding with main processing');
      setIsEntityExtracting(false);
      
      // Clear entity extraction timer
      if (timerIntervalsRef.current.entityExtraction) {
        clearInterval(timerIntervalsRef.current.entityExtraction);
        delete timerIntervalsRef.current.entityExtraction;
      }
      
      // Step 1: AI Translation Agent
      setCurrentStep('🧠 Step 1: AI Translation - Converting your query into atomic searches...');
      setProcessingSteps({ translation: 'processing', execution: 'pending', summary: 'pending' });
      
      const translationResponse = await axios.post('/api/ai-answer/translate', {
        query: query.trim()
      }, {
        timeout: 30000
      });

      if (!translationResponse.data.success) {
        throw new Error(translationResponse.data.error || 'Translation failed');
      }

      // Show translation results immediately
      const translatedQueries = translationResponse.data.translatedQueries;
      setTranslatedQueries(translatedQueries);
      
      // Initialize query execution status for all queries (per-system indexing)
      const initialStatus = {};
      
      // Initialize Procore queries with correct per-system indices
      (translatedQueries.procore || []).forEach((query, index) => {
        initialStatus[`procore_${index}`] = {
          query: query,
          system: 'procore',
          status: 'executing',
          startTime: Date.now()
        };
      });
      
      // Initialize Vista queries with correct per-system indices
      (translatedQueries.vista || []).forEach((query, index) => {
        initialStatus[`vista_${index}`] = {
          query: query,
          system: 'vista',
          status: 'executing',
          startTime: Date.now()
        };
      });
      
      // Initialize Safety Analyst queries with correct per-system indices
      (translatedQueries.safetyAnalyst || []).forEach((query, index) => {
        initialStatus[`safetyAnalyst_${index}`] = {
          query: query,
          system: 'safetyAnalyst',
          status: 'executing',
          startTime: Date.now()
        };
      });
      
      // Initialize SharePoint queries with correct per-system indices
      (translatedQueries.fileSearch || []).forEach((query, index) => {
        initialStatus[`fileSearch_${index}`] = {
          query: query,
          system: 'fileSearch',
          status: 'executing',
          startTime: Date.now()
        };
      });
      
      setQueryExecutionStatus(initialStatus);
      setProcessingSteps(prev => ({ ...prev, translation: 'completed', execution: 'processing' }));
      setCurrentStep('⚡ Step 2: AI Execution - Running queries in parallel across systems...');
      
      // Small delay to show translation completion
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: AI Execution Agent with real-time updates
      const executionResults = await executeQueriesWithRealTimeUpdates(translatedQueries);
      
      setProcessingSteps(prev => ({ ...prev, execution: 'completed', summary: 'processing' }));
      setCurrentStep('📝 Step 3: AI Summary - Generating comprehensive analysis report...');
      
      // Small delay to show execution completion  
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 3: AI Summary Agent
      const summaryResponse = await axios.post('/api/ai-answer/summarize', {
        originalQuery: query.trim(),
        executionResults: executionResults
      }, {
        timeout: 300000 // 5 minute timeout for summary
      });

      if (!summaryResponse.data.success) {
        throw new Error(summaryResponse.data.error || 'Summary generation failed');
      }

      // Show final results with proper structure
      const summaryData = summaryResponse.data;
      const finalReportData = {
        report: summaryData.summaryReport || summaryData.report,
        structuredData: summaryData.structuredData,
        metadata: summaryData.metadata
      };
      
      setFinalReport(finalReportData);
      setProcessingSteps({ translation: 'completed', execution: 'completed', summary: 'completed' });
      setCurrentStep('✅ Analysis complete!');
      
      // FINAL CLEANUP: Stop all polling when summary completes
      console.log('🧹 SUMMARY COMPLETE - IMMEDIATE POLLING CLEANUP');
      
      // Store the current interval IDs before clearing
      const windowIntervalId = window.activePollingInterval;
      const refIntervalId = pollingIntervalRef.current;
      
      console.log('🔍 Checking intervals before cleanup:', {
        windowInterval: windowIntervalId,
        refInterval: refIntervalId,
        areEqual: windowIntervalId === refIntervalId
      });
      
      if (windowIntervalId) {
        clearInterval(windowIntervalId);
        window.activePollingInterval = null;
        console.log(`🛑 CLEARED window.activePollingInterval: ${windowIntervalId}`);
      }
      
      if (refIntervalId) {
        clearInterval(refIntervalId);
        pollingIntervalRef.current = null;
        console.log(`🛑 CLEARED pollingIntervalRef.current: ${refIntervalId}`);
      }
      
      // Extra cleanup - clear by stored ID if they're different
      if (windowIntervalId !== refIntervalId) {
        if (windowIntervalId) clearInterval(windowIntervalId);
        if (refIntervalId) clearInterval(refIntervalId);
        console.log('🛑 EXTRA cleanup: Cleared both different interval IDs');
      }
      
      console.log('✅ SUMMARY POLLING CLEANUP COMPLETE!');
      
      // Also clean up any orphaned polling that might exist
      console.log('🔍 Double-checking for orphaned intervals...');
      setTimeout(() => {
        if (window.activePollingInterval) {
          console.log(`⚠️ Found orphaned polling after cleanup: ${window.activePollingInterval}`);
          clearInterval(window.activePollingInterval);
          window.activePollingInterval = null;
        }
      }, 1000);
      
      // Calculate stats from execution results
      const totalQueries = (executionResults.vista?.length || 0) + (executionResults.procore?.length || 0) + (executionResults.safetyAnalyst?.length || 0) + (executionResults.fileSearch?.length || 0);
      const successfulQueries = (executionResults.vista?.filter(r => r.success).length || 0) + 
                               (executionResults.procore?.filter(r => r.success).length || 0) + 
                               (executionResults.safetyAnalyst?.filter(r => r.success).length || 0) + 
                               (executionResults.fileSearch?.filter(r => r.success).length || 0);
      const totalSystems = [executionResults.vista?.length > 0, executionResults.procore?.length > 0, executionResults.safetyAnalyst?.length > 0, executionResults.fileSearch?.length > 0].filter(Boolean).length;
      
      toast.success(`AI Answer complete! ${successfulQueries}/${totalQueries} successful queries across ${totalSystems} systems`);
      
      console.log('🎉 Final report set:', finalReportData);
      
      // Generate and show contextual follow-up questions when report completes
      const contextualQuestions = generateFollowUpQuestions(finalReportData.report, finalReportData.structuredData);
      setFollowUpQuestions(contextualQuestions);
      setShowFollowUpQuestions(true);
      
    } catch (error) {
      console.error('AI Answer processing error:', error);
      toast.error(`Failed to generate AI Answer: ${error.message}`);
      
      // Mark current step as failed
      const currentProcessingStep = getCurrentProcessingStep();
      setProcessingSteps(prev => ({ 
        ...prev, 
        [currentProcessingStep]: 'failed' 
      }));
      
      // Mark any executing queries as failed
      if (currentProcessingStep === 'execution' && Object.keys(queryExecutionStatus).length > 0) {
        const failedStatus = { ...queryExecutionStatus };
        Object.keys(failedStatus).forEach(key => {
          if (failedStatus[key].status === 'executing') {
            failedStatus[key].status = 'failed';
            failedStatus[key].endTime = Date.now();
            failedStatus[key].error = error.message;
          }
        });
        setQueryExecutionStatus(failedStatus);
      }
      
      setCurrentStep(`Error in ${currentProcessingStep} step: ${error.message}`);
    } finally {
      // FINAL CLEANUP: Always stop polling when handleSearch completes (success or error)
      console.log('🧹 🚨 FINALLY BLOCK EXECUTING - STOPPING ALL POLLING 🚨');
      console.log('🔍 Finally block - Current session:', currentSessionId);
      console.log('🔍 Finally block - Window interval:', window.activePollingInterval);
      console.log('🔍 Finally block - Ref interval:', pollingIntervalRef.current);
      
      if (window.activePollingInterval) {
        clearInterval(window.activePollingInterval);
        window.activePollingInterval = null;
        console.log('🛑 Finally cleanup: Cleared window.activePollingInterval');
      }
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        console.log('🛑 Finally cleanup: Cleared pollingIntervalRef.current');
      }
      
      
      // Clear ALL tracked intervals globally as final safeguard
      if (window.allPollingIntervals && window.allPollingIntervals.size > 0) {
        console.log(`🔄 Finally block: Clearing ${window.allPollingIntervals.size} globally tracked intervals...`);
        
        window.allPollingIntervals.forEach(intervalId => {
          clearInterval(intervalId);
          console.log(`🛑 Finally: Cleared tracked interval: ${intervalId}`);
        });
        
        window.allPollingIntervals.clear();
        console.log('✅ Finally: All tracked intervals cleared');
      }
      
      console.log('✅ Finally cleanup complete - all polling stopped');
      setIsProcessing(false);
    }
  };

  // Export functions for AI Answer reports
  const exportToCSV = async (report) => {
    try {
      console.log('📊 Initiating CSV export for AI Answer report');
      const response = await fetch('/api/ai-answer/export-csv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report: report.report,
          structuredData: report.structuredData,
          metadata: report.metadata,
          originalQuery: query
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use report title or fallback to date-based name
        const reportTitle = report.report?.title || 'AI-Analysis-Report';
        const fileName = `${reportTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report exported to CSV successfully!');
      } else {
        throw new Error('CSV export failed');
      }
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const generateSlides = async (report) => {
    try {
      console.log('🎬 Initiating slide generation for AI Answer report');
      setSlideLoading(true);
      setSlideProgress({ elapsed: 0, remaining: 300, progress: 0, completed: false });
      setSlideCompletionNotified(false); // Reset notification flag
      
      const response = await fetch('/api/ai-answer/generate-slides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report: report.report,
          structuredData: report.structuredData,
          metadata: report.metadata,
          originalQuery: query
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.generationId) {
          console.log(`🎬 Slide generation started with ID: ${result.generationId}`);
          setSlideGenerationId(result.generationId);
          toast.success('Slide generation started!');
          
          // Start progress tracking timer
          const startTime = Date.now();
          const maxTime = 300; // 5 minutes for large slide decks
          
          const progressInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, maxTime - elapsed);
            const progress = Math.min(95, (elapsed / maxTime) * 95); // Cap at 95% until actual completion
            
            setSlideProgress({ 
              elapsed, 
              remaining, 
              progress,
              completed: false 
            });
            
            // Don't auto-complete after timer - keep waiting for actual API response
            if (elapsed >= maxTime) {
              clearInterval(progressInterval);
              // Keep loading state - still waiting for API
            }
          }, 1000);
          
          // Poll for completion status
          let statusInterval;
          const pollSlideStatus = async () => {
            try {
              const statusResponse = await fetch(`/api/ai-answer/slide-status/${result.generationId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                console.log('📊 Status data received:', statusData);
                console.log(`🔍 Status: ${statusData.status}`);
                console.log(`🔗 Gamma URL: ${statusData.gammaUrl || 'NOT FOUND'}`);
                console.log(`📊 Export URL: ${statusData.exportUrl || 'NOT FOUND'}`);
                
                if (statusData.status === 'completed' && !slideCompletionNotified) {
                  console.log('✅ Slide generation completed - showing success notification');
                  console.log('📝 Setting slideProgress with URLs:', {
                    gammaUrl: statusData.gammaUrl,
                    exportUrl: statusData.exportUrl
                  });
                  
                  // Clear both intervals immediately
                  clearInterval(progressInterval);
                  clearInterval(statusInterval);
                  
                  setSlideProgress({
                    elapsed: Math.floor((Date.now() - startTime) / 1000),
                    remaining: 0,
                    progress: 100,
                    completed: true,
                    gammaUrl: statusData.gammaUrl,
                    exportUrl: statusData.exportUrl
                  });
                  setSlideLoading(false);
                  setSlideCompletionNotified(true); // Prevent duplicate notifications
                  toast.success('Slides generated successfully!');
                }
              }
            } catch (statusError) {
              console.error('Slide status check failed:', statusError);
            }
          };
          
          // Check status immediately, then every 10 seconds
          pollSlideStatus(); // First check immediately
          statusInterval = setInterval(pollSlideStatus, 10000);
          
          // Keep polling indefinitely - no timeout stop
          // User can refresh page if needed, but we'll keep checking for completion
          
        } else {
          toast.error('Failed to start slide generation');
          setSlideLoading(false);
        }
      } else {
        throw new Error('Slide generation failed');
      }
    } catch (error) {
      console.error('Slide generation error:', error);
      toast.error('Failed to generate slides');
      setSlideLoading(false);
      setSlideProgress({});
    }
  };

  // Generate contextual follow-up questions based on the report
  const generateFollowUpQuestions = (report, structuredData) => {
    const questions = [];
    
    // Generate questions based on systems that were queried
    if (structuredData?.vista?.length > 0) {
      questions.push("What are the budget variances for the largest projects?");
      questions.push("Which projects have the highest cash flow impact?");
      questions.push("Show me accounts payable aging for automotive projects");
    }
    
    if (structuredData?.procore?.length > 0) {
      questions.push("What safety incidents occurred on these projects this month?");
      questions.push("Show me overdue submittals for Michigan projects");
      questions.push("Which RFIs have the longest response times?");
    }
    
    if (structuredData?.fileSearch?.length > 0) {
      questions.push("Find meeting minutes discussing project 25-111 delays");
      questions.push("What do progress reports say about concrete work?");
      questions.push("Search for change order documentation in project files");
    }
    
    // Add general follow-up questions
    questions.push("Compare project performance across different clients");
    questions.push("What are the top risk factors across all active projects?");
    questions.push("Analyze cost trends for similar project types");
    
    return questions.slice(0, 6); // Show up to 6 relevant questions
  };


  const getCurrentProcessingStep = () => {
    if (processingSteps.translation === 'processing') return 'translation';
    if (processingSteps.execution === 'processing') return 'execution';
    if (processingSteps.summary === 'processing') return 'summary';
    return 'translation';
  };

  const getStepIcon = (step) => {
    switch (processingSteps[step]) {
      case 'processing':
        return <Loader2 className="h-5 w-5 animate-spin text-sylvan-600" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <div className="h-5 w-5 rounded-full bg-gray-300"></div>;
    }
  };

  const getStepStatus = (step) => {
    switch (processingSteps[step]) {
      case 'processing':
        return 'text-sylvan-600 font-semibold';
      case 'completed':
        return 'text-green-600 font-medium';
      case 'failed':
        return 'text-red-600 font-medium';
      default:
        return 'text-gray-500';
    }
  };

  const getStepBackgroundColor = (step) => {
    switch (processingSteps[step]) {
      case 'processing':
        return 'bg-sylvan-50 border-sylvan-200';
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <Bot className="h-10 w-10 text-sylvan-600" />
          <h1 className="text-3xl font-bold text-gray-900">AI Answer</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Ask complex questions about your construction projects. Our AI system will analyze multiple data sources 
          and provide comprehensive answers with supporting data.
        </p>
      </div>


      {/* Search Interface */}
      <div className="max-w-3xl mx-auto mb-8">
        <form onSubmit={handleSearch} className="relative">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask me anything about your construction projects..."
              className="w-full pl-12 pr-24 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-sylvan-500 focus:ring-0 transition-colors"
              disabled={isProcessing}
            />
            <button
              type="submit"
              disabled={isProcessing || !query.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-sylvan-600 text-white px-6 py-2 rounded-lg hover:bg-sylvan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Ask AI'
              )}
            </button>
          </div>
        </form>

        {/* Entity Extraction and Clarification Interface */}
        {showClarification && (
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center">
                <Brain className="h-5 w-5 mr-2" />
                Clarification Needed
              </h3>
              <button
                onClick={() => {
                  setShowClarification(false);
                  setIsProcessing(false);
                  setCurrentStep('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ✕ Cancel
              </button>
            </div>
            
            <p className="text-sm text-blue-600 mb-4">
              {availableProjects.length > 0 
                ? "I found some projects related to your query and generated a refined query. Please review and edit as needed:"
                : "To provide you with the most accurate analysis, I need some clarification about your query:"
              }
            </p>

            {/* Refined Query for Editing */}
            {availableProjects.length > 0 && refinedQuery && (
              <div className="mb-6 bg-white rounded-lg border border-blue-100 p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                    Refined Query
                  </span>
                  The system has refined your query based on the found projects:
                </h4>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Original Query:
                  </label>
                  <div className="p-3 bg-gray-50 rounded-md text-sm text-gray-600">
                    {entityExtraction?.originalQuery || query}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refined Query:
                  </label>
                  <textarea
                    value={refinedQuery}
                    onChange={(e) => setRefinedQuery(e.target.value)}
                    className="w-full h-32 p-3 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 font-mono whitespace-pre-wrap"
                    placeholder="Refined query will appear here..."
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    Edit the refined query to better match your needs. This will be used for the AI Answer analysis.
                  </div>
                </div>
              </div>
            )}


            <div className="space-y-4">
              {clarificationQuestions.map((question, index) => (
                <div key={index} className="bg-white rounded-lg border border-blue-100 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                        {question.priority === 'high' ? 'High' : question.priority === 'medium' ? 'Medium' : 'Low'}
                      </span>
                      {question.question}
                    </h4>
                  </div>
                  
                  {question.options && question.options.length > 0 ? (
                    <div className="space-y-2">
                      {question.options.map((option, optionIndex) => (
                        <label key={optionIndex} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name={`question_${index}`}
                            value={option}
                            checked={userResponses[index] === option}
                            onChange={(e) => {
                              setUserResponses(prev => ({
                                ...prev,
                                [index]: e.target.value
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <textarea
                        placeholder="Please provide your answer..."
                        value={userResponses[index] || ''}
                        onChange={(e) => {
                          setUserResponses(prev => ({
                            ...prev,
                            [index]: e.target.value
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowClarification(false);
                  setIsProcessing(false);
                  setCurrentStep('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleClarificationSubmit}
                disabled={isProcessing || (clarificationQuestions.length > 0 && Object.keys(userResponses).length < clarificationQuestions.length)}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing...
                  </div>
                ) : (
                  availableProjects.length > 0 ? 'Continue with Project Data' : 'Continue Analysis'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Follow-up Questions After Report Completion */}
        {showFollowUpQuestions && followUpQuestions.length > 0 && (
          <div className="mt-6 bg-gradient-to-r from-sylvan-50 to-orange-50 border border-sylvan-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sylvan-800">💡 Continue Your Analysis</h4>
              <button
                onClick={() => setShowFollowUpQuestions(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                ✕ Hide
              </button>
            </div>
            <p className="text-sm text-sylvan-600 mb-3">Based on your results, here are relevant follow-up questions:</p>
            <div className="grid md:grid-cols-2 gap-2">
              {followUpQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setQuery(question);
                    setShowFollowUpQuestions(false);
                  }}
                  className="text-left p-3 bg-white hover:bg-sylvan-50 hover:border-sylvan-300 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                >
                  <span className="text-sylvan-600 font-medium flex-shrink-0">Q{index + 1}:</span>
                  <span className="ml-2">{question}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Example Queries */}
        {!isProcessing && !finalReport && !showFollowUpQuestions && (
          <div className="mt-6">
            <p className="text-sm text-gray-600 mb-3">Try asking complex questions:</p>
            <div className="space-y-2">
              {[
                "What's current status of project 25-480?",
                // "Top 10 active largest contract value projects' AR/AP outstanding, payment terms and safety status",
                // "What are unpaid invoices, cash position, and billing status for projects starting with 24-0?",
              ].map((example, index) => (
                <button
                  key={index}
                  onClick={() => setQuery(example)}
                  className="block w-full text-left px-4 py-3 bg-gray-50 hover:bg-sylvan-50 hover:border-sylvan-300 border border-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
                  disabled={isProcessing}
                >
                  <div className="flex items-start space-x-2">
                    {/* <span className="text-sylvan-600 font-medium flex-shrink-0">Q{index + 1}:</span> */}
                    <span className="flex-1">{example}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div className="max-w-3xl mx-auto mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Loader2 className="h-5 w-5 animate-spin text-sylvan-600" />
              <span className="font-medium text-gray-900">Processing your query...</span>
            </div>
            
            <div className="space-y-4">
              {/* Step 0: Entity Extraction */}
              <div className={`flex items-center space-x-4 p-4 rounded-lg border ${isEntityExtracting ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center space-x-3 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isEntityExtracting ? 'bg-blue-600 text-white' : 'bg-gray-400 text-white'}`}>
                    🔍
                  </div>
                  <div>
                    <div className={`font-medium ${isEntityExtracting ? 'text-blue-800' : 'text-gray-600'}`}>
                      Step 0: Entity Extraction
                    </div>
                    <div className="text-sm text-gray-600">
                      Analyzing your query for project-specific information
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      This step identifies specific projects, locations, and entities to ensure accurate analysis
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 1: Translation */}
              <div className={`flex items-center space-x-4 p-4 rounded-lg border ${getStepBackgroundColor('translation')}`}>
                <div className="flex items-center space-x-3 flex-1">
                  {getStepIcon('translation')}
                  <Brain className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className={`${getStepStatus('translation')}`}>
                      Step 1: AI Translation Agent
                    </div>
                    <div className="text-sm text-gray-600">
                      Converting your query into atomic, system-specific searches
                    </div>
                  </div>
                </div>
                {processingSteps.translation === 'completed' && translatedQueries && (
                  <div className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    {(translatedQueries.procore?.length || 0) + (translatedQueries.vista?.length || 0) + (translatedQueries.fileSearch?.length || 0)} queries
                  </div>
                )}
              </div>

              {/* Step 2: Execution */}
              <div className={`flex items-center space-x-4 p-4 rounded-lg border ${getStepBackgroundColor('execution')}`}>
                <div className="flex items-center space-x-3 flex-1">
                  {getStepIcon('execution')}
                  <Database className="h-5 w-5 text-green-600" />
                  <div>
                    <div className={`${getStepStatus('execution')}`}>
                      Step 2: AI Execution Agent
                    </div>
                    <div className="text-sm text-gray-600">
                      {/* Running queries in parallel across Vista, Procore, Safety Analyst, and SharePoint */}
                      Running queries in parallel across Vista, Procore, Safety, and Analyst
                    </div>
                  </div>
                </div>
                {processingSteps.execution === 'completed' && (
                  <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    Results ready
                  </div>
                )}
              </div>

              {/* Step 3: Summary */}
              <div className={`flex items-center space-x-4 p-4 rounded-lg border ${getStepBackgroundColor('summary')}`}>
                <div className="flex items-center space-x-3 flex-1">
                  {getStepIcon('summary')}
                  <FileText className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className={`${getStepStatus('summary')}`}>
                      Step 3: AI Summary Agent
                    </div>
                    <div className="text-sm text-gray-600">
                      Generating comprehensive analysis and actionable insights
                    </div>
                  </div>
                </div>
                {processingSteps.summary === 'completed' && finalReport && (
                  <div className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                    Report ready
                  </div>
                )}
              </div>

              {/* Query Execution Progress - Show during Step 2 */}
              {processingSteps.execution === 'processing' && Object.keys(queryExecutionStatus).length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-800 mb-4">
                    Query Execution Progress: 
                    <span className="ml-2 text-sylvan-600">
                      {Object.values(queryExecutionStatus).filter(q => q.status === 'completed').length}/
                      {Object.keys(queryExecutionStatus).length} completed
                    </span>
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(queryExecutionStatus).map(([key, queryInfo]) => {
                      const getQueryIcon = (status) => {
                        switch (status) {
                          case 'executing':
                            return (
                              <div className="flex items-center">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600 mr-1" />
                                <span className="text-xs text-blue-600 font-medium">In Progress</span>
                              </div>
                            );
                          case 'completed':
                            return (
                              <div className="flex items-center">
                                <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                                <span className="text-xs text-green-600 font-medium">Completed</span>
                              </div>
                            );
                          case 'failed':
                            return (
                              <div className="flex items-center">
                                <AlertCircle className="h-4 w-4 text-red-600 mr-1" />
                                <span className="text-xs text-red-600 font-medium">Failed</span>
                              </div>
                            );
                          default:
                            return (
                              <div className="flex items-center">
                                <div className="h-4 w-4 rounded-full bg-gray-300 mr-1"></div>
                                <span className="text-xs text-gray-500 font-medium">Pending</span>
                              </div>
                            );
                        }
                      };

                      const getSystemColor = (system) => {
                        switch (system) {
                          case 'vista': return 'text-sylvan-700 bg-sylvan-50 border-sylvan-200';
                          case 'procore': return 'text-green-700 bg-green-50 border-green-200';
                          case 'fileSearch': return 'text-orange-700 bg-orange-50 border-orange-200';
                          default: return 'text-gray-700 bg-gray-50 border-gray-200';
                        }
                      };

                      // Timer removed - status-only display

                      return (
                        <div key={key} className={`flex items-start space-x-3 p-3 rounded-lg border ${getSystemColor(queryInfo.system)}`}>
                          <div className="flex-shrink-0 mt-1">
                            {getQueryIcon(queryInfo.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium capitalize">{queryInfo.system}</span>
                              <div className="flex items-center space-x-2">
                                {/* Status-only display - no result counts */}
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              <div className="font-medium">{queryInfo.query}</div>
                              {/* Debugging info */}
                              <div className="text-xs text-gray-500 mt-1 space-y-1">
                                <div><span className="font-mono">ID:</span> {key}</div>
                                <div><span className="font-mono">Status:</span> {queryInfo.status}</div>
                                {queryInfo.error && (
                                  <div className="text-red-600"><span className="font-mono">Error:</span> {queryInfo.error}</div>
                                )}
                                {queryInfo.retryAttempt > 0 && (
                                  <div><span className="font-mono">Retries:</span> {queryInfo.retryAttempt}</div>
                                )}
                              </div>
                            </div>
                            {queryInfo.error && (
                              <div className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
                                <strong>Error:</strong> {queryInfo.error}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Execution Summary - Show after Step 2 completes */}
              {processingSteps.execution === 'completed' && Object.keys(queryExecutionStatus).length > 0 && (
                <div className="mt-6">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center">
                      <Database className="h-4 w-4 mr-2" />
                      Execution Summary ({Object.values(queryExecutionStatus).filter(q => q.status === 'completed').length}/{Object.keys(queryExecutionStatus).length} successful)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Group queries by system */}
                      {['vista', 'procore', 'safetyAnalyst', 'fileSearch'].map(system => {
                        const systemQueries = Object.values(queryExecutionStatus).filter(q => q.system === system);
                        if (systemQueries.length === 0) return null;
                        
                        const successCount = systemQueries.filter(q => q.status === 'completed').length;
                        const systemColors = {
                          vista: 'bg-blue-100 text-blue-800',
                          procore: 'bg-green-100 text-green-800',
                          safetyAnalyst: 'bg-red-100 text-red-800',
                          fileSearch: 'bg-orange-100 text-orange-800'
                        };
                        
                        return (
                          <div key={system} className="space-y-2">
                            <div className={`text-xs font-medium px-2 py-1 rounded-full ${systemColors[system]}`}>
                              {system === 'safetyAnalyst' ? 'Safety Analyst' : system.charAt(0).toUpperCase() + system.slice(1)}: {successCount}/{systemQueries.length} successful
                            </div>
                            {systemQueries.map((q, i) => {
                              return (
                                <div key={i} className={`text-xs p-2 rounded border-l-2 ${q.status === 'completed' ? 'bg-green-50 text-green-800 border-green-400' : q.status === 'failed' ? 'bg-red-50 text-red-800 border-red-400' : 'bg-blue-50 text-blue-800 border-blue-400'}`}>
                                  <div className="flex items-start space-x-2">
                                    <span className="flex-shrink-0 mt-0.5">
                                      {q.status === 'completed' ? '✅' : q.status === 'failed' ? '❌' : '⏳'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-xs mb-1">Q{i + 1}: {q.query}</div>
                                      <div className="space-y-1">
                                        <div><span className="font-mono text-gray-500">ID:</span> <span className="font-mono">{Object.keys(queryExecutionStatus).find(key => queryExecutionStatus[key] === q) || `${system}_${i}`}</span></div>
                                        <div><span className="font-mono text-gray-500">Status:</span> <span className="capitalize font-medium">{q.status}</span></div>
                                        {q.error && (
                                          <div><span className="font-mono text-gray-500">Error:</span> <span className="text-red-600">{q.error}</span></div>
                                        )}
                                        {q.retryAttempt > 0 && (
                                          <div><span className="font-mono text-gray-500">Retries:</span> <span>{q.retryAttempt}</span></div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Current Step Status */}
            {currentStep && (
              <div className="mt-6">
                <div className="p-4 bg-gradient-to-r from-sylvan-50 to-orange-50 rounded-lg border border-sylvan-200">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin text-sylvan-600" />
                    <p className="text-sylvan-800 text-sm font-medium">{currentStep}</p>
                  </div>
                  
                  {/* Show progress bar */}
                  <div className="mt-3 w-full bg-sylvan-200 rounded-full h-2">
                    <div 
                      className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ 
                        width: `${
                          processingSteps.translation === 'completed' && processingSteps.execution === 'completed' && processingSteps.summary === 'completed' ? 100 :
                          processingSteps.translation === 'completed' && processingSteps.execution === 'completed' ? 85 :
                          processingSteps.translation === 'completed' && processingSteps.execution === 'processing' ? 60 :
                          processingSteps.translation === 'completed' ? 35 :
                          processingSteps.translation === 'processing' ? 15 : 0
                        }%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Translated Queries Preview - Show prominently when available */}
      {translatedQueries && (
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold flex items-center text-gray-900">
                <Brain className="h-5 w-5 mr-2 text-purple-600" />
                AI Generated Queries
                <span className="ml-2 bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                  {(translatedQueries.procore?.length || 0) + (translatedQueries.vista?.length || 0) + (translatedQueries.safetyAnalyst?.length || 0) + (translatedQueries.fileSearch?.length || 0)} total
                </span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Your question was broken down into these specific atomic queries for optimal results
              </p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Procore Queries */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-green-800 flex items-center">
                      <Database className="h-4 w-4 mr-1.5" />
                      Procore (Operational)
                    </h4>
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      {translatedQueries.procore?.length || 0}
                    </span>
                  </div>
                  {translatedQueries.procore?.length > 0 ? (
                    translatedQueries.procore.map((q, i) => (
                      <div key={i} className="text-sm p-3 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-green-600 font-medium">Q{i + 1}:</span> {q}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                      No operational queries needed
                    </div>
                  )}
                </div>

                {/* Vista Queries */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-blue-800 flex items-center">
                      <Database className="h-4 w-4 mr-1.5" />
                      Vista (Financial)
                    </h4>
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                      {translatedQueries.vista?.length || 0}
                    </span>
                  </div>
                  {translatedQueries.vista?.length > 0 ? (
                    translatedQueries.vista.map((q, i) => (
                      <div key={i} className="text-sm p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-blue-600 font-medium">Q{i + 1}:</span> {q}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                      No financial queries needed
                    </div>
                  )}
                </div>

                {/* Safety Analyst Queries */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-red-800 flex items-center">
                      <span className="mr-1.5">🛡️</span>
                      Safety Analyst
                    </h4>
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                      {translatedQueries.safetyAnalyst?.length || 0}
                    </span>
                  </div>
                  {translatedQueries.safetyAnalyst?.length > 0 ? (
                    translatedQueries.safetyAnalyst.map((q, i) => (
                      <div key={i} className="text-sm p-3 bg-red-50 rounded-lg border border-red-200">
                        <span className="text-red-600 font-medium">Q{i + 1}:</span> {q}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                      No safety queries needed
                    </div>
                  )}
                </div>

                {/* SharePoint Queries */}
                <div className="space-y-3" style={{display: 'none'}}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-orange-800 flex items-center">
                      <FileText className="h-4 w-4 mr-1.5" />
                      SharePoint (25-111 data)
                    </h4>
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                      {translatedQueries.fileSearch?.length || 0}
                    </span>
                  </div>
                  {translatedQueries.fileSearch?.length > 0 ? (
                    translatedQueries.fileSearch.map((q, i) => (
                      <div key={i} className="text-sm p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <span className="text-orange-600 font-medium">Q{i + 1}:</span> {q}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded-lg">
                      No document queries needed
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final Report */}
      {finalReport && finalReport.report && (
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Multi-Tab Summary Report */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold flex items-center">
                  <Bot className="h-6 w-6 mr-2 text-sylvan-600" />
                AI Analysis Report
              </h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  Generated from {finalReport?.metadata?.totalDataPoints || 0} data points
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => exportToCSV(finalReport)}
                    className="inline-flex items-center px-3 py-1.5 border border-sylvan-300 text-sm font-medium rounded-md text-sylvan-700 bg-white hover:bg-sylvan-50 focus:outline-none focus:ring-2 focus:ring-sylvan-500"
                  >
                    📊 Export CSV
                  </button>
                  <button
                    onClick={() => generateSlides(finalReport)}
                    disabled={slideLoading}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {slideLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      '🎬 Generate Slides'
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Slide Generation Progress */}
            {slideLoading && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Loader2 className="h-5 w-5 animate-spin text-sylvan-600" />
                  <h4 className="font-medium text-gray-800">Generating AI Answer Presentation</h4>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>⏱️ {slideProgress.elapsed || 0}s elapsed</span>
                    <span>⏳ {slideProgress.remaining || 120}s remaining</span>
                  </div>
                  
                  <div className="w-full bg-sylvan-200 rounded-full h-2">
                    <div 
                      className="bg-sylvan-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${slideProgress.progress || 0}%` }}
                    ></div>
                  </div>
                  
                  <div className="text-xs text-sylvan-600">
                    Generating comprehensive construction presentation with Sylvan theme...
                  </div>
                </div>
              </div>
            )}

            {/* Slide Generation Complete */}
            {slideProgress.completed && !slideLoading && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <span className="font-medium text-green-800">Presentation Generated Successfully!</span>
                  </div>
                  <div className="flex space-x-2">
                    {slideProgress.gammaUrl && (
                      <button
                        onClick={() => window.open(slideProgress.gammaUrl, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                      >
                        🔗 View Slides
                      </button>
                    )}
                    {slideProgress.exportUrl && (
                      <a
                        href={slideProgress.exportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
                        title="Open PPTX in new tab"
                      >
                        💾 Open PPTX
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            
              {(() => {
                console.log('🔍 Parsing report sections, finalReport.report type:', typeof finalReport?.report);
                console.log('🔍 finalReport.report content preview:', JSON.stringify(finalReport?.report)?.substring(0, 200) + '...');
                
                const reportSections = parseReportSections(finalReport?.report);
                console.log('🔍 Generated sections:', reportSections?.length || 0, 'sections');
                console.log('🔍 Section titles:', reportSections?.map(s => s.title) || 'none');
              
              return (
                <>
                  {/* Summary Tab Navigation */}
                  <div className="flex flex-wrap gap-2 p-4 bg-gray-50 border-b border-gray-200">
                    {reportSections && reportSections.length > 0 ? (
                      reportSections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => setActiveSummaryTab(section.id)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                          activeSummaryTab === section.id
                            ? 'bg-sylvan-600 text-white shadow-md'
                            : 'text-sylvan-700 hover:text-sylvan-900 hover:bg-sylvan-100 border border-sylvan-200'
                        }`}
                      >
                        <span>{section.icon}</span>
                        <span>{section.title}</span>
                      </button>
                      ))
                    ) : (
                      <div className="text-gray-500 italic">No report sections available</div>
                    )}
                  </div>
                  
                  {/* Summary Tab Content */}
                  <div className="p-6">
                    {reportSections && reportSections.length > 0 ? (
                      reportSections.map((section) => (
                      <div
                        key={section.id}
                        className={activeSummaryTab === section.id ? 'block' : 'hidden'}
                      >
                        <div className="prose prose-gray max-w-none">
                          {section.content ? (
                            <div dangerouslySetInnerHTML={{ 
                              __html: section.content
                                .replace(/## (.*?)\n/g, '<h3 class="text-lg font-bold text-gray-900 mt-6 mb-3 border-b border-gray-200 pb-2">$1</h3>')
                                .replace(/### (.*?)\n/g, '<h4 class="text-md font-semibold text-gray-800 mt-4 mb-2">$1</h4>')
                                .replace(/- (.*?)\n/g, '<li class="ml-4 mb-2 text-gray-700 leading-relaxed">• $1</li>')
                                .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
                                .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>')
                                .replace(/\n\n/g, '<br/><br/>')
                                .replace(/\n/g, '<br/>')
                            }} />
                          ) : (
                            <div className="text-gray-500 italic">
                              No content available for this section
                            </div>
                          )}
                        </div>
                      </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic p-6">No report content available for tabs</div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Multi-Tab Raw Data Results */}
          {finalReport?.structuredData && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold flex items-center">
                  <Database className="h-6 w-6 mr-2 text-green-600" />
                  Raw Query Results & Data
                </h3>
                <span className="text-sm text-gray-500">
                  Detailed results from each system with debugging info
                </span>
              </div>
              
              {/* Data Tab Navigation - Enhanced */}
              <div className="bg-gradient-to-r from-sylvan-50 to-blue-50 border-b border-gray-200">
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Database className="h-5 w-5 text-sylvan-600 mr-2" />
                    Data Sources & Analysis
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Access detailed results from each system. Click on any tab to explore specific data sources.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {finalReport.structuredData?.vista && finalReport.structuredData.vista.length > 0 && (
                      <button
                        onClick={() => setActiveDataTab('vista')}
                        className={`px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center space-x-3 shadow-sm hover:shadow-md ${
                          activeDataTab === 'vista'
                            ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                            : 'text-blue-700 hover:text-blue-900 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-300'
                        }`}
                      >
                        <span className="text-lg">💰</span>
                        <div className="text-left">
                          <div className="font-bold">Vista Financial</div>
                          <div className="text-xs opacity-75">{finalReport.structuredData.vista.length} queries</div>
                        </div>
                      </button>
                    )}
                    
                    {finalReport.structuredData?.procore && finalReport.structuredData.procore.length > 0 && (
                      <button
                        onClick={() => setActiveDataTab('procore')}
                        className={`px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center space-x-3 shadow-sm hover:shadow-md ${
                          activeDataTab === 'procore'
                            ? 'bg-green-600 text-white shadow-lg transform scale-105'
                            : 'text-green-700 hover:text-green-900 hover:bg-green-100 border-2 border-green-200 hover:border-green-300'
                        }`}
                      >
                        <span className="text-lg">🏗️</span>
                        <div className="text-left">
                          <div className="font-bold">Procore Operational</div>
                          <div className="text-xs opacity-75">{finalReport.structuredData.procore.length} queries</div>
                        </div>
                      </button>
                    )}

                    {finalReport.structuredData?.safetyAnalyst && finalReport.structuredData.safetyAnalyst.length > 0 && (
                      <button
                        onClick={() => setActiveDataTab('safetyAnalyst')}
                        className={`px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center space-x-3 shadow-sm hover:shadow-md ${
                          activeDataTab === 'safetyAnalyst'
                            ? 'bg-red-600 text-white shadow-lg transform scale-105'
                            : 'text-red-700 hover:text-red-900 hover:bg-red-100 border-2 border-red-200 hover:border-red-300'
                        }`}
                      >
                        <span className="text-lg">🛡️</span>
                        <div className="text-left">
                          <div className="font-bold">Safety Analyst</div>
                          <div className="text-xs opacity-75">{finalReport.structuredData.safetyAnalyst.length} queries</div>
                        </div>
                      </button>
                    )}
                    
                    {/* {finalReport.structuredData?.fileSearch && finalReport.structuredData.fileSearch.length > 0 && (
                      <button
                        onClick={() => setActiveDataTab('fileSearch')}
                        className={`px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center space-x-3 shadow-sm hover:shadow-md ${
                          activeDataTab === 'fileSearch'
                            ? 'bg-orange-600 text-white shadow-lg transform scale-105'
                            : 'text-orange-700 hover:text-orange-900 hover:bg-orange-100 border-2 border-orange-200 hover:border-orange-300'
                        }`}
                      >
                        <span className="text-lg">📄</span>
                        <div className="text-left">
                          <div className="font-bold">SharePoint</div>
                          <div className="text-xs opacity-75">{finalReport.structuredData.fileSearch.length} queries</div>
                        </div>
                      </button>
                    )} */}
                  </div>
                </div>
              </div>
              
              {/* Data Tab Content */}
              <div className="p-6">
                
                {/* Vista Data Tab */}
                <div className={`space-y-6 ${activeDataTab === 'vista' ? 'block' : 'hidden'}`}>
                  
                  {finalReport.structuredData?.vista && finalReport.structuredData.vista.length > 0 ? (
                    <div className="space-y-4">
                      {finalReport.structuredData.vista.map((result, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-900">{result.query}</h5>
                          <span className={`text-sm px-2 py-1 rounded ${
                            result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {result.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        
                        {result.success && (
                          <div>
                            {/* Show Vista summary/analysis regardless of data */}
                            {result.response && (
                              <div className="text-sm text-gray-700 mb-3 p-3 bg-gray-50 rounded">
                                <div className="font-medium text-gray-800 mb-2">Vista Analysis:</div>
                                <div className="whitespace-pre-wrap">{result.response}</div>
                              </div>
                            )}
                            
                            {/* Show data table only if data exists */}
                            {result.data && result.data.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm text-gray-600 mb-2">
                                  {result.dataCount || result.data.length} records found
                                </p>
                                <DataTable data={result.data} columns={result.columns || Object.keys(result.data[0] || {})} />
                              </div>
                            )}
                            
                            {/* Show when no data but query succeeded */}
                            {(!result.data || result.data.length === 0) && (
                              <div className="text-sm text-gray-500 italic mb-3">
                                No data records found for this query
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show SQL Query for Vista results */}
                        {result.success && result.sqlQuery && (
                          <div className="mb-3">
                            <div className="text-xs text-gray-500 mb-1 flex items-center">
                              <span className="mr-2">💻</span>
                              <span className="font-medium">Generated SQL Query:</span>
                            </div>
                            <div className="bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
                              <pre className="whitespace-pre-wrap">{result.sqlQuery}</pre>
                            </div>
                          </div>
                        )}
                        
                        {result.error && (
                          <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
                            Error: {result.error}
                          </div>
                        )}
                      </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">No Vista financial data available</div>
                  )}
                </div>

                {/* Procore Data Tab */}
                <div className={`space-y-6 ${activeDataTab === 'procore' ? 'block' : 'hidden'}`}>
                  {finalReport.structuredData?.procore && finalReport.structuredData.procore.length > 0 ? (
                    <div className="space-y-4">
                      {finalReport.structuredData.procore.map((result, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-900">{result.query}</h5>
                          <span className={`text-sm px-2 py-1 rounded ${
                            result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {result.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        
                        {result.success && (
                          <div>
                            {/* Show Procore summary/analysis regardless of data */}
                            {result.response && (
                              <div className="text-sm text-gray-700 mb-3 p-3 bg-gray-50 rounded">
                                <div className="font-medium text-gray-800 mb-2">Procore Analysis:</div>
                                <div className="whitespace-pre-wrap">{result.response}</div>
                              </div>
                            )}
                            
                            {/* Show data table only if data exists */}
                            {result.data && result.data.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm text-gray-600 mb-2">
                                  {result.dataCount || result.data.length} records found (Michigan projects only)
                                </p>
                                <DataTable data={result.data} columns={result.columns || Object.keys(result.data[0] || {})} />
                              </div>
                            )}
                            
                            {/* Show when no data but query succeeded */}
                            {(!result.data || result.data.length === 0) && (
                              <div className="text-sm text-gray-500 italic mb-3">
                                No operational records found for this query (Michigan projects only)
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show SQL Query for Procore results if available */}
                        {result.success && result.sqlQuery && (
                          <div className="mb-3">
                            <div className="text-xs text-gray-500 mb-1 flex items-center">
                              <span className="mr-2">💻</span>
                              <span className="font-medium">Generated SQL Query:</span>
                            </div>
                            <div className="bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
                              <pre className="whitespace-pre-wrap">{result.sqlQuery}</pre>
                            </div>
                          </div>
                        )}
                        
                        {/* Show SQL Query for Procore results if available */}
                        {result.success && (result.sqlQuery || result.metadata?.sqlQuery) && (
                          <div className="mb-3">
                            <div className="text-xs text-gray-500 mb-1 flex items-center">
                              <span className="mr-2">💻</span>
                              <span className="font-medium">Generated SQL Query:</span>
                            </div>
                            <div className="bg-gray-900 text-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
                              <pre className="whitespace-pre-wrap">{result.sqlQuery || result.metadata?.sqlQuery}</pre>
                            </div>
                          </div>
                        )}
                        
                        {result.error && (
                          <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
                            Error: {result.error}
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">No Procore operational data available</div>
                  )}
                </div>

                {/* Safety Analyst Data Tab */}
                <div className={`space-y-6 ${activeDataTab === 'safetyAnalyst' ? 'block' : 'hidden'}`}>
                  {finalReport.structuredData?.safetyAnalyst && finalReport.structuredData.safetyAnalyst.length > 0 ? (
                    <div className="space-y-4">
                      {finalReport.structuredData.safetyAnalyst.map((result, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium text-gray-900">{result.query}</h5>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {result.success ? 'Success' : 'Failed'}
                              </span>
                              <span className="text-xs text-gray-500">{result.dataCount} records</span>
                            </div>
                          </div>

                          {result.analysis && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Analysis:</h6>
                              <div className="text-sm text-gray-600 bg-red-50 p-3 rounded">
                                {result.analysis}
                              </div>
                            </div>
                          )}

                          {result.explanation && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Explanation:</h6>
                              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                                {result.explanation}
                              </div>
                            </div>
                          )}

                          {result.data && result.data.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Data ({result.data.length} records):</h6>
                              <div className="bg-gray-50 p-3 rounded max-h-64 overflow-y-auto">
                                <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                                  {JSON.stringify(result.data.slice(0, 5), null, 2)}
                                  {result.data.length > 5 && `\n... and ${result.data.length - 5} more records`}
                                </pre>
                              </div>
                            </div>
                          )}

                          {result.sqlQuery && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">SQL Query:</h6>
                              <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto">
                                {result.sqlQuery}
                              </div>
                            </div>
                          )}

                          {result.followUpQuestions && result.followUpQuestions.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Follow-up Questions:</h6>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {result.followUpQuestions.map((question, qIndex) => (
                                  <li key={qIndex} className="flex items-start">
                                    <span className="text-gray-400 mr-2">•</span>
                                    <span>{question}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {result.error && (
                            <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                              <strong>Error:</strong> {result.error}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <span className="text-4xl mb-4 block">🛡️</span>
                      <p>No safety analysis data available</p>
                    </div>
                  )}
                </div>

                {/* SharePoint Data Tab */}
                <div className={`space-y-6 ${activeDataTab === 'fileSearch' ? 'block' : 'hidden'}`} style={{display: 'none'}}>
                  {finalReport.structuredData?.fileSearch && finalReport.structuredData.fileSearch.length > 0 ? (
                    <div className="space-y-4">
                      {finalReport.structuredData.fileSearch.map((result, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-900">{result.query}</h5>
                          <span className={`text-sm px-2 py-1 rounded ${
                            result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {result.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        
                        {result.success && (
                          <div>
                            {result.summary && (
                              <div className="mb-4 p-3 bg-gray-50 rounded">
                                <h6 className="font-medium text-gray-800 mb-2">Document Summary:</h6>
                                <p className="text-sm text-gray-700">{result.summary}</p>
                              </div>
                            )}
                            
                            {result.results && result.results.length > 0 && (
                              <div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {result.resultCount} documents from {result.filesProcessed} files
                                </p>
                                <div className="max-h-40 overflow-y-auto">
                                  {result.results.slice(0, 5).map((fileResult, fileIndex) => (
                                    <div key={fileIndex} className="text-xs p-2 bg-gray-50 border-l-2 border-orange-200 mb-2">
                                      <div className="font-medium">{fileResult.file_path}</div>
                                      <div className="text-gray-600">{(fileResult.text_preview || fileResult.text || '').substring(0, 100)}...</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {result.error && (
                          <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
                            Error: {result.error}
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">No file search data available</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAnswer;
